import React from 'react';
import { List, Show, useListContext, useShowContext, useGetList } from 'react-admin';
import { useBreadcrumbContext } from '../components/layout/AppBreadcrumb';
import {
  CheckCircle,
  XCircle,
  Eye,
  MousePointer,
  Bell,
  Smartphone,
  AlertCircle,
  Clock,
  User,
} from 'lucide-react';
import { SearchIntegration } from '../components/common/SearchIntegration';
import { ReactAdminPagination } from '../components/ui/react-admin-pagination';
import { ListScreen } from '../components/ui/list-screen';
import { StatCard, StatRow } from '../components/ui/stat-card';
import { SegmentedTabs } from '../components/ui/segmented-tabs';
import { DataTable, DataColumn } from '../components/ui/data-table';
import { SortMenu } from '../components/ui/sort-menu';
import { Filters, FilterTokens, FilterFieldConfig } from '../components/ui/filter-controls';
import { AvatarName } from '../components/ui/data-cells';
import { cn } from '../lib/utils';

/* ============================================================
   Notification logs — Latio design system (derived, read-only log table).
   ============================================================ */

const NOTIFICATION_TYPES = [
  { id: 'match_reminder_30m', name: 'Match Reminder (30m)' },
  { id: 'match_started', name: 'Match Started' },
  { id: 'match_cancelled', name: 'Match Cancelled' },
  { id: 'player_join_request', name: 'Join Request' },
  { id: 'player_left', name: 'Player Left' },
  { id: 'request_accepted', name: 'Request Accepted' },
  { id: 'request_denied', name: 'Request Denied' },
  { id: 'player_kicked', name: 'Player Kicked' },
  { id: 'review_request', name: 'Review Request' },
  { id: 'admin_announcement', name: 'Admin Announcement' },
  { id: 'marketing_campaign', name: 'Marketing Campaign' },
  { id: 'system_maintenance', name: 'System Maintenance' },
  { id: 'feature_update', name: 'Feature Update' },
];
const typeName = (id?: string) => NOTIFICATION_TYPES.find((t) => t.id === id)?.name || id || '—';

interface NotificationLog {
  id: number;
  user_name?: string;
  user_email?: string;
  notification_type?: string;
  platform?: string;
  delivered?: boolean;
  opened?: boolean;
  clicked?: boolean;
  sent_at?: string;
  opened_at?: string;
  clicked_at?: string;
  error_details?: string;
  device_token?: string;
  notification_id?: string;
}

/* delivery status: clicked→indigo, opened→warning, delivered→success, failed→error */
function deliveryMeta(r: NotificationLog) {
  if (r.clicked) return { label: 'Clicked', cls: 'bg-indigo-tint text-indigo', dot: 'bg-indigo', Icon: MousePointer };
  if (r.opened) return { label: 'Opened', cls: 'bg-warning-bg text-warning', dot: 'bg-warning', Icon: Eye };
  if (r.delivered) return { label: 'Delivered', cls: 'bg-success-bg text-success', dot: 'bg-success', Icon: CheckCircle };
  return { label: 'Failed', cls: 'bg-error-bg text-error', dot: 'bg-error', Icon: XCircle };
}
function DeliveryBadge({ record }: { record: NotificationLog }) {
  const m = deliveryMeta(record);
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs font-semibold', m.cls)}>
      <m.Icon className="size-3" />
      {m.label}
    </span>
  );
}

const fmtDateTime = (v?: string | null) =>
  v ? new Date(v).toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

/* ---- List ---- */
const LOG_TABS = [
  { value: 'all', label: 'All' },
  { value: 'delivered_only', label: 'Delivered' },
  { value: 'opened_only', label: 'Opened' },
  { value: 'clicked_only', label: 'Clicked' },
];
const LOG_FILTERS: FilterFieldConfig[] = [
  { field: 'notification_type', label: 'Type', icon: Bell, options: NOTIFICATION_TYPES.map((t) => ({ value: t.id, label: t.name })) },
];
const LOG_SORT = [
  { field: 'sent_at', label: 'Sent' },
  { field: 'notification_type', label: 'Type' },
  { field: 'platform', label: 'Platform' },
  { field: 'id', label: 'ID' },
];
const LOG_SORT_LABELS = Object.fromEntries(LOG_SORT.map((f) => [f.field, f.label]));
const ENGAGEMENT_KEYS = ['delivered_only', 'opened_only', 'clicked_only'];

const LogTabs = () => {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const current = ENGAGEMENT_KEYS.find((k) => filterValues[k]) || 'all';
  return (
    <SegmentedTabs
      tabs={LOG_TABS}
      value={current}
      onChange={(v) => {
        const next = { ...filterValues };
        ENGAGEMENT_KEYS.forEach((k) => delete next[k]);
        if (v !== 'all') next[v] = true;
        setFilters(next, displayedFilters);
      }}
    />
  );
};

const LogStats = () => {
  const base = { pagination: { page: 1, perPage: 1 }, sort: { field: 'sent_at', order: 'DESC' } } as const;
  const all = useGetList('notification-logs', { ...base, filter: {} });
  const delivered = useGetList('notification-logs', { ...base, filter: { delivered_only: true } });
  const opened = useGetList('notification-logs', { ...base, filter: { opened_only: true } });
  const clicked = useGetList('notification-logs', { ...base, filter: { clicked_only: true } });
  const n = (v?: number) => (v === undefined ? '—' : v);
  return (
    <StatRow>
      <StatCard primary label="Total sent" value={n(all.total)} />
      <StatCard label="Delivered" value={n(delivered.total)} />
      <StatCard label="Opened" value={n(opened.total)} />
      <StatCard label="Clicked" value={n(clicked.total)} />
    </StatRow>
  );
};

const LogsListBody = () => {
  const columns: DataColumn<NotificationLog>[] = [
    { key: 'id', header: 'ID', sortable: true, width: '1px', cell: (r) => <span className="tabular-nums text-ink-2">#{r.id}</span> },
    {
      key: 'user',
      header: 'User',
      sortable: true,
      sortField: 'user_email',
      cell: (r) => <AvatarName name={r.user_name || 'Unknown'} subtext={r.user_email} size={28} />,
    },
    { key: 'notification_type', header: 'Type', sortable: true, cell: (r) => <span className="text-ink-2">{typeName(r.notification_type)}</span> },
    { key: 'delivered', header: 'Status', sortable: true, cell: (r) => <DeliveryBadge record={r} /> },
    {
      key: 'platform',
      header: 'Platform',
      sortable: true,
      cell: (r) => (
        <span className="inline-flex items-center gap-1.5 text-ink-2">
          <Smartphone className="size-3.5" />
          {r.platform || 'Unknown'}
        </span>
      ),
    },
    { key: 'sent_at', header: 'Sent', sortable: true, cell: (r) => fmtDateTime(r.sent_at) },
    {
      key: 'details',
      header: 'Details',
      cell: (r) =>
        r.error_details ? (
          <span className="inline-flex items-center gap-1 text-[13px] text-error">
            <AlertCircle className="size-3.5" /> Error
          </span>
        ) : (
          <span className="text-[13px] text-ink-3">OK</span>
        ),
    },
  ];
  return (
    <ListScreen
      stats={<LogStats />}
      tabs={<LogTabs />}
      actions={
        <>
          <Filters fields={LOG_FILTERS} />
          <SortMenu fields={LOG_SORT} />
        </>
      }
      tokens={<FilterTokens fields={LOG_FILTERS} sortLabels={LOG_SORT_LABELS} />}
    >
      <DataTable columns={columns} rowClick="show" emptyMessage="No notification logs found." />
      <div className="pt-1">
        <ReactAdminPagination />
      </div>
    </ListScreen>
  );
};

export const NotificationLogsList = (props: any) => (
  <List {...props} actions={false} empty={false} pagination={false} sort={{ field: 'sent_at', order: 'DESC' }} perPage={50} component="div">
    <SearchIntegration filterKey="q" />
    <LogsListBody />
  </List>
);

/* ---- Detail ---- */
function DetailCard({ title, icon: Icon, children, className }: { title?: string; icon?: any; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-card border border-hairline bg-background p-5', className)}>
      {title && (
        <h3 className="mb-3 flex items-center gap-2 text-[15px] font-bold text-ink">
          {Icon && <Icon className="size-4 text-ink-2" />}
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}
function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-[13px] text-ink-2">{label}</span>
      <span className="text-right text-sm font-medium text-ink">{value}</span>
    </div>
  );
}
function Flag({ on, label }: { on?: boolean; label: string }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-[13px] text-ink-2">{label}</span>
      {on ? (
        <span className="inline-flex items-center gap-1 text-sm font-medium text-success"><CheckCircle className="size-3.5" /> Yes</span>
      ) : (
        <span className="text-sm text-ink-3">No</span>
      )}
    </div>
  );
}

const NotificationLogShowContent = () => {
  const { record, isLoading } = useShowContext<NotificationLog>();
  const { setData } = useBreadcrumbContext();

  React.useEffect(() => {
    if (record) setData({ resourceId: record.id.toString(), resourceName: `Log #${record.id}`, resourceType: 'session' });
  }, [record, setData]);

  if (isLoading) return <div className="p-6 text-center text-ink-2">Loading log…</div>;
  if (!record) return <div className="p-6 text-center text-ink-2">Log not found</div>;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="flex size-[60px] items-center justify-center rounded-card bg-indigo-tint text-indigo">
            <Bell className="size-7" />
          </span>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-[24px] font-bold leading-none text-ink" style={{ letterSpacing: '-0.02em' }}>
                Notification log #{record.id}
              </h1>
              <DeliveryBadge record={record} />
            </div>
            <p className="mt-1 text-sm text-ink-2">{typeName(record.notification_type)} · {fmtDateTime(record.sent_at)}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="space-y-6">
          <DetailCard title="Recipient" icon={User}>
            <div className="divide-y divide-hairline">
              <Fact label="Name" value={record.user_name || 'Unknown'} />
              <Fact
                label="Email"
                value={record.user_email || '—'}
              />
              <Fact label="Platform" value={record.platform || 'Unknown'} />
              <Fact label="Type" value={typeName(record.notification_type)} />
            </div>
          </DetailCard>

          <DetailCard title="Delivery" icon={CheckCircle}>
            <div className="divide-y divide-hairline">
              <Flag on={record.delivered} label="Delivered" />
              <Flag on={record.opened} label="Opened" />
              <Flag on={record.clicked} label="Clicked" />
            </div>
          </DetailCard>

          {record.error_details && (
            <DetailCard title="Error" icon={AlertCircle} className="border-error/30 bg-error-bg">
              <p className="whitespace-pre-wrap break-words text-sm text-error">{record.error_details}</p>
            </DetailCard>
          )}
        </div>

        <div className="space-y-6">
          <DetailCard title="Timeline" icon={Clock}>
            <div className="divide-y divide-hairline">
              <Fact label="Sent" value={fmtDateTime(record.sent_at)} />
              <Fact label="Opened" value={fmtDateTime(record.opened_at)} />
              <Fact label="Clicked" value={fmtDateTime(record.clicked_at)} />
            </div>
          </DetailCard>

          <DetailCard title="Technical">
            <div className="divide-y divide-hairline">
              <Fact label="Notification ID" value={record.notification_id || '—'} />
              <div className="py-1.5">
                <div className="text-[13px] text-ink-2">Device token</div>
                <p className="mt-0.5 break-all font-mono text-[12px] text-ink">{record.device_token || '—'}</p>
              </div>
            </div>
          </DetailCard>
        </div>
      </div>
    </div>
  );
};

export const NotificationLogsShow = (props: any) => (
  <Show {...props} actions={false} title=" " component="div">
    <NotificationLogShowContent />
  </Show>
);
